import React, { useState, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import AddCircleOutline from "@material-ui/icons/AddCircleOutline";
import RemoveCircleOutline from "@material-ui/icons/RemoveCircleOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    maxWidth: 720,
    margin: "0 auto",
    ...theme.scrollbarStyles,
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  submitBtn: {
    marginTop: theme.spacing(2),
  },
  responseBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    fontFamily: "monospace",
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
}));

const TestInteractive = () => {
  const classes = useStyles();
  const location = useLocation();
  const paperRef = useRef(null);
  const [ticketId, setTicketId] = useState("");
  const [type, setType] = useState("button");
  const [bodyText, setBodyText] = useState("Escolha uma opção:");
  const [buttonText, setButtonText] = useState("Ver opções");
  const [buttons, setButtons] = useState([
    { id: "opt1", title: "Opção 1" },
    { id: "opt2", title: "Opção 2" },
  ]);
  const [sections, setSections] = useState([
    {
      title: "Seção 1",
      rows: [
        { id: "row1", title: "Item 1", description: "" },
        { id: "row2", title: "Item 2", description: "" },
      ],
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (paperRef.current) paperRef.current.scrollTop = 0;
  }, []);

  // Preencher ticketId pela query string (ex: /test-interactive?ticketId=uuid)
  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const tid = params.get("ticketId");
    if (tid && !ticketId) setTicketId(tid);
  }, [location.search, ticketId]);

  const updateButton = (index, field, value) => {
    const next = [...buttons];
    next[index] = { ...next[index], [field]: value };
    setButtons(next);
  };

  const addButton = () => {
    if (buttons.length >= 3) return;
    setButtons([...buttons, { id: `opt${buttons.length + 1}`, title: `Opção ${buttons.length + 1}` }]);
  };

  const removeButton = (index) => {
    if (buttons.length <= 1) return;
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const updateRow = (sectionIndex, rowIndex, field, value) => {
    const next = [...sections];
    next[sectionIndex].rows[rowIndex] = { ...next[sectionIndex].rows[rowIndex], [field]: value };
    setSections(next);
  };

  const addRow = (sectionIndex) => {
    const next = [...sections];
    const rows = next[sectionIndex].rows;
    next[sectionIndex].rows = [...rows, { id: `r${Date.now()}`, title: "Novo item", description: "" }];
    setSections(next);
  };

  const removeRow = (sectionIndex, rowIndex) => {
    const next = [...sections];
    if (next[sectionIndex].rows.length <= 1) return;
    next[sectionIndex].rows = next[sectionIndex].rows.filter((_, i) => i !== rowIndex);
    setSections(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tid = ticketId.trim();
    if (!tid) {
      toast.error("Informe o ID ou UUID do ticket.");
      return;
    }
    if (type !== "pix" && !bodyText.trim()) {
      toast.error("Informe o texto da mensagem.");
      return;
    }
    if (type === "button" && (buttons.length === 0 || buttons.some((b) => !b.id.trim() || !b.title.trim()))) {
      toast.error("Preencha pelo menos um botão (id e título).");
      return;
    }
    if (type === "list") {
      const hasRows = sections.some((s) => s.rows && s.rows.length > 0);
      if (!hasRows || sections.some((s) => s.rows.some((r) => !r.id.trim() || !r.title.trim()))) {
        toast.error("Preencha pelo menos uma seção com linhas (id e título).");
        return;
      }
    }

    setLoading(true);
    setResponse(null);
    try {
      let payload;
      if (type === "pix") {
        payload = { type: "pix", bodyText: bodyText.trim() || "Sua chave PIX para recebimento:" };
      } else if (type === "button") {
        payload = { type: "button", bodyText: bodyText.trim(), buttons: buttons.map((b) => ({ id: b.id.trim(), title: b.title.trim().substring(0, 20) })) };
      } else {
        payload = {
          type: "list",
          bodyText: bodyText.trim(),
          buttonText: buttonText.trim() || "Ver opções",
          sections: sections.map((s) => ({
            title: s.title,
            rows: s.rows.map((r) => ({
              id: r.id.trim(),
              title: r.title.trim().substring(0, 24),
              description: r.description ? r.description.trim().substring(0, 72) : undefined,
            })),
          })),
        };
      }
      const { data } = await api.post(`/messages/${tid}/send-interactive`, payload);
      setResponse({ success: true, ...data });
      toast.success("Mensagem interativa enviada.");
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Erro ao enviar.";
      setResponse({ success: false, error: msg });
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper ref={paperRef} className={classes.mainPaper}>
      <Typography variant="h6" className={classes.title}>
        Testar envio de Botões e Lista
      </Typography>
      <Typography color="textSecondary" style={{ marginBottom: 16 }}>
        Use o ID ou UUID do ticket (você pode copiar da URL ao abrir um atendimento).
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="ID ou UUID do ticket"
          value={ticketId}
          onChange={(e) => setTicketId(e.target.value)}
          placeholder="Ex: abc123-uuid ou 42"
          className={classes.field}
          variant="outlined"
          size="small"
        />

        <Typography gutterBottom>Tipo de mensagem</Typography>
        <RadioGroup row value={type} onChange={(e) => setType(e.target.value)} className={classes.field}>
          <FormControlLabel value="button" control={<Radio />} label="Botões (até 3)" />
          <FormControlLabel value="list" control={<Radio />} label="Lista" />
          <FormControlLabel value="pix" control={<Radio />} label="PIX" />
        </RadioGroup>

        <TextField
          fullWidth
          label={type === "pix" ? "Mensagem opcional (antes da chave PIX)" : "Texto da mensagem"}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          className={classes.field}
          variant="outlined"
          size="small"
          multiline
          rows={2}
          placeholder={type === "pix" ? "Ex: Sua chave PIX para recebimento:" : undefined}
        />

        {type === "pix" && (
          <Typography color="textSecondary" className={classes.field}>
            Será enviada a chave PIX configurada nas configurações da empresa (com botão &quot;Copiar chave PIX&quot;).
          </Typography>
        )}

        {type === "list" && (
          <TextField
            fullWidth
            label="Texto do botão que abre a lista"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            className={classes.field}
            variant="outlined"
            size="small"
            placeholder="Ex: Ver opções"
          />
        )}

        {type === "button" && (
          <Box className={classes.sectionBox}>
            <Typography variant="subtitle2" gutterBottom>
              Botões (máx. 3, título até 20 caracteres)
            </Typography>
            {buttons.map((btn, i) => (
              <div key={i} className={classes.row}>
                <TextField
                  size="small"
                  label="ID"
                  value={btn.id}
                  onChange={(e) => updateButton(i, "id", e.target.value)}
                  style={{ width: 120 }}
                />
                <TextField
                  size="small"
                  label="Título"
                  value={btn.title}
                  onChange={(e) => updateButton(i, "title", e.target.value)}
                  style={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => removeButton(i)} disabled={buttons.length <= 1}>
                  <RemoveCircleOutline />
                </IconButton>
              </div>
            ))}
            {buttons.length < 3 && (
              <Button size="small" startIcon={<AddCircleOutline />} onClick={addButton}>
                Adicionar botão
              </Button>
            )}
          </Box>
        )}

        {type === "list" && (
          <Box className={classes.sectionBox}>
            <Typography variant="subtitle2" gutterBottom>
              Seções e itens
            </Typography>
            {sections.map((sec, si) => (
              <Box key={si} mb={2}>
                <TextField
                  size="small"
                  label="Título da seção"
                  value={sec.title}
                  onChange={(e) => {
                    const next = [...sections];
                    next[si].title = e.target.value;
                    setSections(next);
                  }}
                  className={classes.field}
                  fullWidth
                />
                {sec.rows.map((row, ri) => (
                  <div key={ri} className={classes.row}>
                    <TextField
                      size="small"
                      label="ID"
                      value={row.id}
                      onChange={(e) => updateRow(si, ri, "id", e.target.value)}
                      style={{ width: 100 }}
                    />
                    <TextField
                      size="small"
                      label="Título"
                      value={row.title}
                      onChange={(e) => updateRow(si, ri, "title", e.target.value)}
                      style={{ width: 160 }}
                    />
                    <TextField
                      size="small"
                      label="Descrição (opcional)"
                      value={row.description || ""}
                      onChange={(e) => updateRow(si, ri, "description", e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <IconButton size="small" onClick={() => removeRow(si, ri)} disabled={sec.rows.length <= 1}>
                      <RemoveCircleOutline />
                </IconButton>
                  </div>
                ))}
                <Button size="small" startIcon={<AddCircleOutline />} onClick={() => addRow(si)}>
                  Adicionar item
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.submitBtn}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : null}
        >
          {loading ? "Enviando…" : "Enviar"}
        </Button>
      </form>

      {response && (
        <Box className={classes.responseBox} mt={2}>
          {response.success ? (
            <>Sucesso: {JSON.stringify(response, null, 2)}</>
          ) : (
            <>Erro: {response.error}</>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default TestInteractive;
